import type { CollectorSummary, Frame } from "@contracts/admin";
import { useQuery } from "@tanstack/react-query";
import { Eye, Film, Filter, ImageOff, Search, X } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { apiRequest } from "@frontend/api/client";
import { Button, EmptyState, ErrorState, LoadingState, Modal, PageHeader, StatusBadge } from "@frontend/components/ui";
import { formatBytes, formatDate } from "@frontend/lib/format";

interface PreviewState {
  frame: Frame;
  url: string;
  expiresAt: string;
}

export function FramesPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [collectorId, setCollectorId] = useState(() => searchParams.get("collector_id") ?? "");
  const [cameraId, setCameraId] = useState(() => searchParams.get("camera_id") ?? "");
  const [status, setStatus] = useState(() => searchParams.get("status") ?? "");
  const [search, setSearch] = useState("");
  const [preview, setPreview] = useState<PreviewState | null>(null);
  const [previewLoading, setPreviewLoading] = useState<string | null>(null);
  const [previewError, setPreviewError] = useState<string | null>(null);

  const collectors = useQuery({
    queryKey: ["collectors", "frame-filter"],
    queryFn: () => apiRequest<{ data: CollectorSummary[] }>("/api/v1/admin/collectors?limit=200"),
  });
  useEffect(() => {
    const next = new URLSearchParams();
    if (collectorId) next.set("collector_id", collectorId);
    if (cameraId) next.set("camera_id", cameraId);
    if (status) next.set("status", status);
    setSearchParams(next, { replace: true });
  }, [cameraId, collectorId, setSearchParams, status]);

  const requestParams = new URLSearchParams({ limit: "100" });
  if (collectorId) requestParams.set("collector_id", collectorId);
  if (cameraId) requestParams.set("camera_id", cameraId);
  if (status) requestParams.set("status", status);
  const frames = useQuery({
    queryKey: ["frames", collectorId, cameraId, status],
    queryFn: () => apiRequest<{ data: Frame[] }>(`/api/v1/admin/frames?${requestParams.toString()}`),
    refetchInterval: 30_000,
  });

  const visible = useMemo(() => {
    const normalized = search.trim().toLowerCase();
    return (frames.data?.data ?? []).filter((frame) =>
      !normalized || [frame.filename, frame.object_key, frame.capture_id]
        .some((value) => value.toLowerCase().includes(normalized)),
    );
  }, [frames.data, search]);
  const hasFilters = Boolean(collectorId || cameraId || status || search);

  function clearFilters() {
    setCollectorId("");
    setCameraId("");
    setStatus("");
    setSearch("");
    setPreviewError(null);
  }

  async function openPreview(frame: Frame) {
    setPreviewError(null);
    setPreviewLoading(frame.id);
    try {
      const result = await apiRequest<{ frame: Frame; read: { url: string; expires_at: string } }>(`/api/v1/admin/frames/${frame.id}/read-url`, { method: "POST" });
      setPreview({ frame: result.frame, url: result.read.url, expiresAt: result.read.expires_at });
    } catch (error) {
      setPreviewError(error instanceof Error ? error.message : "Não foi possível abrir o frame.");
    } finally {
      setPreviewLoading(null);
    }
  }

  return (
    <>
      <PageHeader title="Frames" />
      <section aria-label="Filtros de frames" className="filter-panel">
        <span className="filter-panel__title"><Filter size={17} /> Filtros</span>
        <label><span>Coletor</span><select onChange={(event) => { setCollectorId(event.target.value); setCameraId(""); }} value={collectorId}><option value="">Todos os coletores</option>{collectors.data?.data.map((collector) => <option key={collector.id} value={collector.id}>{collector.name}</option>)}</select></label>
        <label><span>ID da câmera</span><input onChange={(event) => setCameraId(event.target.value)} placeholder="cam_…" value={cameraId} /></label>
        <label><span>Estado</span><select onChange={(event) => setStatus(event.target.value)} value={status}><option value="">Todos</option><option value="completed">Concluídos</option><option value="pending">Pendentes</option></select></label>
        <label className="filter-search"><span>Buscar</span><span className="search-input"><Search size={16} /><input onChange={(event) => setSearch(event.target.value)} placeholder="Arquivo ou object key" value={search} /></span></label>
      </section>

      <div className="frames-result-bar">
        <span aria-live="polite">{visible.length} {visible.length === 1 ? "frame" : "frames"}</span>
        {hasFilters ? <Button variant="ghost" onClick={clearFilters}><X size={16} /> Limpar filtros</Button> : null}
      </div>

      {previewError ? <div className="form-alert" role="alert">{previewError}</div> : null}
      {frames.isLoading ? <LoadingState label="Carregando frames" /> : null}
      {frames.isError ? <ErrorState message="A lista de frames não respondeu." retry={() => void frames.refetch()} /> : null}
      {!frames.isLoading && !frames.isError && visible.length === 0 ? <EmptyState title="Nenhum frame" /> : null}

      {visible.length > 0 ? <div className="data-table-wrap"><table className="data-table frames-table"><caption className="sr-only">Frames encontrados</caption><thead><tr><th>Captura</th><th>Coletor / câmera</th><th>Estado</th><th>Tamanho</th><th>Objeto</th><th aria-label="Preview" /></tr></thead><tbody>{visible.map((frame) => <tr key={frame.id}><td data-label="Captura"><div className="entity-cell"><span className="entity-icon"><Film size={17} /></span><div><strong>{formatDate(frame.captured_at_ms)}</strong><small className="mono-small">{frame.filename}</small></div></div></td><td data-label="Origem"><div className="stacked-id"><span>{shortId(frame.collector_id)}</span><small>{shortId(frame.camera_id)}</small></div></td><td data-label="Estado"><StatusBadge tone={frame.status === "completed" ? "success" : "warning"}>{frame.status === "completed" ? "Concluído" : "Pendente"}</StatusBadge></td><td data-label="Tamanho">{formatBytes(frame.size_bytes)}</td><td data-label="Objeto"><span className="object-key" title={frame.object_key}>{frame.object_key}</span></td><td><Button aria-label={`Abrir preview de ${frame.filename}`} disabled={frame.status !== "completed" || previewLoading === frame.id} variant="ghost" onClick={() => void openPreview(frame)}>{previewLoading === frame.id ? "Abrindo…" : <><Eye size={16} /> Preview</>}</Button></td></tr>)}</tbody></table></div> : null}

      <Modal open={preview !== null} title={preview?.frame.filename ?? "Preview"} description={preview ? `URL privada válida até ${formatDate(preview.expiresAt)}` : undefined} onClose={() => setPreview(null)}>
        {preview ? <div className="frame-preview"><img alt={`Frame capturado em ${formatDate(preview.frame.captured_at_ms)}`} decoding="async" src={preview.url} /><dl><div><dt>Coletor</dt><dd title={preview.frame.collector_id}>{preview.frame.collector_id}</dd></div><div><dt>Câmera</dt><dd title={preview.frame.camera_id}>{preview.frame.camera_id}</dd></div><div><dt>Object key</dt><dd title={preview.frame.object_key}>{preview.frame.object_key}</dd></div><div><dt>SHA-256</dt><dd title={preview.frame.sha256 ?? "Não informado"}>{preview.frame.sha256 ?? "Não informado"}</dd></div></dl></div> : <div className="frame-preview__empty"><ImageOff /> Preview indisponível</div>}
      </Modal>
    </>
  );
}

function shortId(value: string): string { return value.length > 20 ? `${value.slice(0, 12)}…${value.slice(-5)}` : value; }
